import { create } from 'zustand';

export type ReportKind = 'bug' | 'idea';
export type ReportResult = 'sent' | 'expired' | 'limited' | 'failed';

export interface KnownIssue {
  id: string;
  title: string;
  body: string;
  status: 'open' | 'fixed';
  createdAt: number;
  updatedAt: number;
  fixedAt: number | null;
}

interface SupportState {
  sending: boolean;
  error: string | null;
  knownIssues: KnownIssue[];
  sendReport: (input: {
    kind: ReportKind;
    message: string;
    projectId?: string;
  }) => Promise<ReportResult>;
  loadKnownIssues: () => Promise<void>;
}

const API = '/api/platform/support';
let inFlight: Promise<ReportResult> | null = null;

async function postReport(input: {
  kind: ReportKind;
  message: string;
  projectId?: string;
}): Promise<{ result: ReportResult; error: string | null }> {
  const token = localStorage.getItem('platform_token');
  if (!token) return { result: 'expired', error: null };
  try {
    const res = await fetch(`${API}/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        ...input,
        app: 'dashboard',
        page: window.location.pathname,
        userAgent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
      }),
    });
    if (res.ok) return { result: 'sent', error: null };
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    if (res.status === 401) return { result: 'expired', error: null };
    if (res.status === 429) return { result: 'limited', error: data.error ?? null };
    return { result: 'failed', error: data.error ?? null };
  } catch {
    return { result: 'failed', error: null };
  }
}

export const useSupportStore = create<SupportState>((set) => ({
  sending: false,
  error: null,
  knownIssues: [],

  sendReport: (input) => {
    if (inFlight) return inFlight;
    set({ sending: true, error: null });
    inFlight = postReport(input).then(({ result, error }) => {
      inFlight = null;
      set({ sending: false, error });
      return result;
    });
    return inFlight;
  },

  loadKnownIssues: async () => {
    try {
      const res = await fetch(`${API}/known-issues`);
      if (!res.ok) return;
      const data = (await res.json()) as { issues: KnownIssue[] };
      set({ knownIssues: data.issues });
    } catch {
      // The page shows its empty state; nothing to recover.
    }
  },
}));
